import React, {Component} from 'react';
import {Line} from "react-chartjs-2";
import {DateRangePicker} from "react-dates";
import connect from "react-redux/es/connect/connect";
import {BeatLoader} from "react-spinners";
import {FormattedMessage, injectIntl} from "react-intl";
import * as moment from "moment";

// @mui/material components
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select
}  from '@mui/material'

// styles
import priceChartStyle from "assets/jss/views/product/priceChartStyle";
import {primaryColor} from "../../../../../assets/jss/main";

// actions
import {retrieveVariationPriceStockData} from "../../../../../actions/variation/priceStockData";
import {withStyles} from "@mui/styles";

class PriceChart extends Component {

    constructor(props) {
        super(props);

        const date = new Date();
        const endDate = moment(date);

        date.setDate(date.getDate() - 20);
        const startDate = moment(date);

        this.state = {
            startDate: startDate,
            initialStartDate: startDate,
            endDate: endDate,
            initialEndDate: endDate,
            unit: 'day',
            chartData: {},
        }
    }
    componentDidMount() {
        this.loadChartData();
    }

    componentDidUpdate(prevProps) {
        if (this.props.currentVariation !== prevProps.currentVariation) {
            this.loadChartData();
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps, nextContext) {
        if (this.props.variationPriceStockData !== nextProps.variationPriceStockData) {
            const data = nextProps.variationPriceStockData || {};

            // Collect inventory series (all keys except 'price')
            const inventoryKeys = Object.keys(data).filter(k => k !== 'price');

            // Build the X axis by unifying all timestamps across price and inventories
            const collectKeys = (obj) => obj ? Object.keys(obj) : [];
            let xIndexes;
            if (this.state.unit === 'detail') {
                const allKeys = [
                    ...collectKeys(data.price),
                    ...inventoryKeys.flatMap(k => collectKeys(data[k]))
                ];
                xIndexes = [...new Set(allKeys)];
            } else {
                // For aggregated units, also unify all keys to avoid holes between series
                const allKeys = [
                    ...collectKeys(data.price),
                    ...inventoryKeys.flatMap(k => collectKeys(data[k]))
                ];
                xIndexes = [...new Set(allKeys)];
            }

            // Ensure X axis labels (dates) are chronologically sorted
            const dateFormats = [
                'YYYY-MM-DD HH:mm:ss',
                'YYYY-M-D HH:mm:ss',
                'YYYY-MM-DD',
                'YYYY-M-D',
                'YYYY-MM',
                'YYYY-M',
                'YYYY'
            ];
            xIndexes.sort((a, b) => {
                const ma = moment(a, dateFormats, true);
                const mb = moment(b, dateFormats, true);
                const va = ma.isValid() ? ma.valueOf() : Number.NEGATIVE_INFINITY;
                const vb = mb.isValid() ? mb.valueOf() : Number.NEGATIVE_INFINITY;
                if (Number.isNaN(va) || Number.isNaN(vb)) {
                    // Fallback to string comparison if parsing fails
                    return a.localeCompare(b);
                }
                return va - vb;
            });

            // Helper to align any series on xIndexes, inserting null when missing
            const alignSeries = (seriesObj) => {
                const source = seriesObj || {};
                return xIndexes.map(idx => (Object.prototype.hasOwnProperty.call(source, idx) ? source[idx] : null));
            };

            const priceData = alignSeries(data.price);

            // Prepare color palette for inventories
            const palette = [
                { bg: 'rgb(0, 82, 203)', border: 'rgba(0, 82, 203, .8)' },
                { bg: 'rgb(255, 159, 64)', border: 'rgba(255, 159, 64, .8)' },
                { bg: 'rgb(153, 102, 255)', border: 'rgba(153, 102, 255, .8)' },
                { bg: 'rgb(255, 99, 132)', border: 'rgba(255, 99, 132, .8)' },
                { bg: 'rgb(75, 192, 192)', border: 'rgba(75, 192, 192, .8)' },
                { bg: 'rgb(54, 162, 235)', border: 'rgba(54, 162, 235, .8)' },
            ];

            const datasets = [
                {
                    label: this.props.intl.formatMessage({id: "variation.graph.label.price"}),
                    data: priceData,
                    fill: false,
                    backgroundColor: 'rgb(0, 214, 86)',
                    borderColor: 'rgba(0, 214, 86, .8)',
                    yAxisID: 'y-axis-1',
                    tension: 0.1,
                    pointBorderWidth: 4,
                    spanGaps: true,
                },
            ];

            inventoryKeys.forEach((invKey, idx) => {
                const colors = palette[idx % palette.length];
                datasets.push({
                    label: invKey,
                    data: alignSeries(data[invKey]),
                    fill: false,
                    backgroundColor: colors.bg,
                    borderColor: colors.border,
                    yAxisID: 'y-axis-2',
                    tension: 0.1,
                    pointBorderWidth: 4,
                    spanGaps: true,
                });
            });

            const chartData = {
                labels: xIndexes,
                datasets: datasets,
            };

            this.setState({ chartData });
        }
    }

    handleDateChange(dates) {
        if (dates.startDate === null || dates.endDate === null) {
            dates = {startDate: this.state.initialStartDate, endDate: this.state.initialEndDate}
        }

        this.setState({startDate: dates.startDate, endDate: dates.endDate}, function () {
            this.loadChartData();
        })
    };

    handleChartPrecisionChange = (event) => {
        this.setState({unit: event.target.value}, function () {
            this.loadChartData();
        })
    };

    loadChartData = () => {
        const start = this.state.startDate;
        const end = this.state.endDate;

        const startStr = start ? start.format("YYYY-MM-DD") : undefined;
        const endStr = end ? end.format("YYYY-MM-DD") : undefined;

        this.props.retrieveVariationPriceStockData(
            this.props.currentVariation.id,
            startStr,
            endStr,
            this.state.unit
        );
    }

    render() {
        const {classes} = this.props;

        const options = {
            scales: {
                yAxes: [
                    {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        id: 'y-axis-1',
                    },
                    {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        id: 'y-axis-2',
                    },
                ],
            },
        };

        return (
            <div className={classes.container}>
                <div className={classes.datePicker}>
                    <DateRangePicker
                        startDateId="startDate"
                        endDateId="endDate"
                        startDatePlaceholderText={"-"}
                        endDatePlaceholderText={"-"}
                        startDate={this.state.startDate}
                        endDate={this.state.endDate}
                        onDatesChange={({startDate, endDate}) => {
                            this.setState({startDate, endDate})
                        }}
                        focusedInput={this.state.focusedInput}
                        onFocusChange={(focusedInput) => {
                            this.setState({focusedInput})
                        }}
                        onClose={(dates) => this.handleDateChange(dates)}
                        displayFormat={"D MMMM YYYY"}
                        block={true}
                        readOnly={true}
                        isOutsideRange={() => false}
                        showDefaultInputIcon={true}
                        showClearDates={true}
                        reopenPickerOnClearDates={true}
                        required={true}
                        hideKeyboardShortcutsPanel={true}
                        withFullScreenPortal={window.innerWidth < 768}
                        numberOfMonths={2}
                        orientation={window.innerWidth < 768 ? "vertical" : "horizontal"}
                        anchorDirection={"right"}
                        minimumNights={0}
                    />

                    <FormControl variant="standard" className={classes.formControl}>
                        <InputLabel id="simple-select-label"><FormattedMessage id={"variation.graph.unit.label"}/></InputLabel>
                        <Select
                            variant="standard"
                            labelId="simple-select-label"
                            id="simple-select"
                            value={this.state.unit}
                            onChange={this.handleChartPrecisionChange}>
                            <MenuItem value={'day'}><FormattedMessage id={"variation.graph.unit.day"}/></MenuItem>
                            <MenuItem value={'month'}><FormattedMessage id={"variation.graph.unit.month"}/></MenuItem>
                            <MenuItem value={'year'}><FormattedMessage id={"variation.graph.unit.year"}/></MenuItem>
                            <MenuItem value={'detail'}><FormattedMessage id={"variation.graph.unit.detail"}/></MenuItem>
                        </Select>
                    </FormControl>
                </div>

                <b><FormattedMessage id={"variation.graph.title"}/></b><br/>

                {this.props.variationPriceStockDataLoading ?
                    <div className={classes.beatLoader}>
                        <BeatLoader
                            sizeUnit={"px"}
                            size={10}
                            color={primaryColor}
                            loading={true}
                        />
                    </div>
                :
                    <Line data={this.state.chartData} options={options} />
                }
            </div>
        );
    }
}

const mapStateToProps = (state) => {

    return {
        variationPriceStockData: state.variation.priceStockData.retrieveVariationPriceStockData,
        variationPriceStockDataLoading: state.variation.priceStockData.retrieveVariationPriceStockDataLoading,
    }
}

const mapDispatchToProps = dispatch => ({
    retrieveVariationPriceStockData: (variationId, startDate, endDate, unit) => dispatch(retrieveVariationPriceStockData(variationId, startDate, endDate, unit)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(priceChartStyle)(injectIntl(PriceChart)));
